import React, { useEffect, useRef, useState } from 'react';
import { getWalletWithdrawals, withdrawWallet } from '../services/api';

const STATUSES = { pending: 'En attente', processing: 'En cours', completed: 'Effectué', failed: 'Échoué' };
const money = value => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} F CFA`;

export default function WalletWithdrawals({ wallet, settings, revision, onBalanceChange, onWithdrawal }) {
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [requestKey, setRequestKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('WAVE_CI');
  const [phone, setPhone] = useState('');
  const [review, setReview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const submittingRef = useRef(false);
  const attemptedRef = useRef(false);
  const enabled = !!settings?.enabled;
  const hasSettings = !!settings;

  useEffect(() => {
    if (!hasSettings) return;
    let active = true;
    setLoading(true);
    setHistoryError('');
    getWalletWithdrawals(page).then(response => {
      if (!active) return;
      setRecords(response.withdrawals || []);
      setMeta(response.meta);
      setRequestKey(response.request_key || '');
      if (response.wallet) onBalanceChange(response.wallet);
    }).catch(err => {
      if (active) setHistoryError(err.message || 'Impossible de charger les retraits.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [hasSettings, page, revision, refresh, onBalanceChange]);

  useEffect(() => {
    if (!records.some(record => ['pending', 'processing'].includes(record.status))) return;
    const timer = setInterval(() => setRefresh(value => value + 1), 30000);
    return () => clearInterval(timer);
  }, [records]);

  const prepare = event => {
    event.preventDefault();
    setError('');
    const numeric = Number(amount);
    if (!/^\d+$/.test(amount) || !Number.isSafeInteger(numeric) || numeric < settings.min_amount || numeric > settings.max_amount || numeric > Number(wallet.balance)) {
      setError('Saisissez un montant entier compris dans les limites de retrait et votre solde disponible.');
      return;
    }
    const normalizedPhone = phone.trim().replace(/[\s()-]/g, '');
    const prefix = settings.phone_prefix || '+225';
    if (!normalizedPhone.startsWith(prefix) || !/^\+\d+$/.test(normalizedPhone) || normalizedPhone.length !== prefix.length + settings.phone_digits) {
      setError(`Saisissez un numéro mobile money au format ${prefix}, suivi de ${settings.phone_digits} chiffres.`);
      return;
    }
    setReview({ amount: numeric, payment_method: method, phone_number: normalizedPhone, idempotency_key: requestKey });
  };

  const confirm = async () => {
    if (!review || submittingRef.current) return;
    submittingRef.current = true;
    attemptedRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const result = await withdrawWallet(review);
      setNotice(result.message);
      attemptedRef.current = false;
      setOpen(false);
      setReview(null);
      setAmount('');
      setRequestKey('');
      setPage(1);
      setRefresh(value => value + 1);
      onWithdrawal();
    } catch (err) {
      setError(Object.values(err.errors || {}).flat()[0] || err.message || 'Impossible de vérifier cette demande. Réessayez la même demande.');
      // A lost response must reuse the reviewed payload and request key.
      if (err.errors) {
        attemptedRef.current = false;
        setReview(null);
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!settings) return null;

  return (
    <section className="wallet-withdrawals" aria-label="Retraits du portefeuille">
      <div className="wallet-withdrawal-heading"><h2>Retirer mon argent</h2><button type="button" disabled={!enabled || loading || !requestKey || (!review && Number(wallet?.balance) < settings.min_amount) || submitting} onClick={() => { setOpen(true); if (!attemptedRef.current) setError(''); setNotice(''); }}>Retirer de l’argent</button></div>
      <p>Vos retraits sont envoyés sur votre compte mobile money. Les montants en cours de transfert restent réservés jusqu’à confirmation.</p>
      {!enabled && <p>Les retraits ne sont pas encore disponibles.</p>}
      {enabled && Number(wallet?.balance) < settings.min_amount && <p>Le retrait minimum est de {money(settings.min_amount)}.</p>}
      {notice && <p className="wallet-withdrawal-notice" role="status">{notice}</p>}
      {error && <p className="wallet-error" role="alert">{error}</p>}
      {open && !review && <form className="wallet-withdrawal-form" onSubmit={prepare}>
        <label>Montant à retirer (F CFA)<input name="withdrawal_amount" aria-label="Montant à retirer" type="number" min={settings.min_amount} max={Math.min(settings.max_amount, Math.floor(Number(wallet.balance)))} step="1" required value={amount} onChange={event => setAmount(event.target.value)} /></label>
        <label>Compte mobile money<select aria-label="Compte mobile money" value={method} onChange={event => setMethod(event.target.value)}>{Object.entries(settings.payment_methods || {}).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
        <label>Numéro mobile money<input aria-label="Numéro mobile money" type="tel" autoComplete="tel" placeholder={`${settings.phone_prefix}0700000000`} required value={phone} onChange={event => setPhone(event.target.value)} /></label>
        <small>Entre {money(settings.min_amount)} et {money(settings.max_amount)}, dans la limite de votre solde disponible.</small>
        <div className="wallet-actions"><button type="submit" disabled={!requestKey || loading}>Vérifier le retrait</button><button type="button" onClick={() => setOpen(false)}>Annuler</button></div>
      </form>}
      {open && review && <div className="wallet-withdrawal-review" aria-label="Vérification du retrait">
        <h3>Vérifiez le destinataire</h3><strong>{money(review.amount)}</strong><p>{settings.payment_methods[review.payment_method]} · {review.phone_number}</p>
        <div className="wallet-actions"><button type="button" disabled={submitting} onClick={confirm}>{submitting ? 'Envoi en cours…' : error ? 'Réessayer la même demande' : 'Confirmer le retrait'}</button>{!attemptedRef.current && <button type="button" disabled={submitting} onClick={() => setReview(null)}>Modifier</button>}<button type="button" disabled={submitting} onClick={() => setOpen(false)}>Fermer</button></div>
      </div>}
      <div className="wallet-withdrawal-heading"><h3>Historique des retraits</h3><button type="button" disabled={loading} onClick={() => setRefresh(value => value + 1)}>Actualiser les retraits</button></div>
      {historyError && <p className="wallet-error" role="alert">{historyError}</p>}
      {loading && records.length === 0 ? <p role="status">Chargement des retraits…</p> : !historyError && records.length === 0 ? <p>Aucun retrait effectué.</p> : records.map(record => <article className="wallet-transaction" key={record.id}>
        <div className="wallet-transaction-info"><strong>Retrait · {settings.payment_methods[record.payment_method] || record.payment_method}</strong><p>{record.phone_number}</p><small>{record.reference}</small><time dateTime={record.created_at}>{new Date(record.created_at).toLocaleString('fr-FR')}</time></div>
        <div className="wallet-transaction-value"><strong>− {money(record.amount)}</strong><span className={`wallet-status wallet-status-${record.status}`}>{STATUSES[record.status] || record.status}</span></div>
      </article>)}
      {meta?.last_page > 1 && <nav className="wallet-pagination" aria-label="Pages de retraits"><button type="button" disabled={loading || page <= 1} onClick={() => setPage(value => value - 1)}>Retraits précédents</button><span>{meta.current_page} / {meta.last_page}</span><button type="button" disabled={loading || page >= meta.last_page} onClick={() => setPage(value => value + 1)}>Retraits suivants</button></nav>}
    </section>
  );
}
